import type { Difficulty, MCQQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-4-3a：兩位數 × 兩位數直式（四上進階版）。
 * 四上第三章：兩位 × 兩位，包含較大乘數與多次進位。
 *
 * difficulty 1：12–29 × 11–19（乘積 < 600）
 * difficulty 2：20–59 × 20–39（乘積 < 2500）
 * difficulty 3：30–99 × 30–99（乘積可達 9801）
 */
export function generateMul2x2Up(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  let a: number, b: number;
  if (difficulty === 1) {
    a = randInt(rand, 12, 29);
    b = randInt(rand, 11, 19);
  } else if (difficulty === 2) {
    a = randInt(rand, 20, 59);
    b = randInt(rand, 20, 39);
  } else {
    a = randInt(rand, 30, 99);
    b = randInt(rand, 30, 99);
  }

  const correct = a * b;
  const unitsRow = a * (b % 10);
  const tensRowNoShift = a * Math.floor(b / 10);

  // Distractors
  const distractors = new Set<number>();
  const noShift = unitsRow + tensRowNoShift;
  if (noShift !== correct && noShift > 0) distractors.add(noShift);
  distractors.add(correct + 10);
  if (correct - 10 > 0) distractors.add(correct - 10);

  const offsets = [50, -50, 100, -100, 200];
  let oi = 0;
  while (distractors.size < 3 && oi < offsets.length) {
    const c = correct + offsets[oi]!;
    if (c > 0 && c !== correct && !distractors.has(c)) distractors.add(c);
    oi++;
  }
  while (distractors.size < 3) {
    const offset = randInt(rand, 1, 50);
    const c = correct + offset;
    if (c > 0 && c !== correct && !distractors.has(c)) distractors.add(c);
  }

  const optionList = [correct, ...Array.from(distractors).slice(0, 3)];
  const shuffled = [...optionList].sort((x, y) => mulberry32(seed + x)() - mulberry32(seed + y)());

  return {
    kind: 'mcq',
    prompt: `${a} × ${b} = ?`,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation: `直式：${a}×${b % 10}=${unitsRow}，${a}×${Math.floor(b / 10)}=${tensRowNoShift}（左移一位=${tensRowNoShift * 10}），合計 ${unitsRow}+${tensRowNoShift * 10}=${correct}`,
    kp: 'math.mul.2x2.4up',
  };
}
